export class AudioPlayer {
  private ctx: AudioContext;
  private buffers = new Map<string, AudioBuffer>();

  constructor() {
    // webkitAudioContext fallback only matters for iOS < 14.5, but it's cheap
    const Ctx =
      window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new Ctx();
  }

  /** The underlying AudioContext (for custom one-shot synthesis). */
  get context(): AudioContext {
    return this.ctx;
  }

  /**
   * Call this once from a real user gesture handler (pointerdown / touchend / click).
   * iOS creates AudioContexts in the "suspended" state and only allows resuming
   * them synchronously inside a gesture.
   */
  unlock(): Promise<void> {
    if (this.ctx.state === 'suspended') {
      return this.ctx.resume();
    }
    return Promise.resolve();
  }

  /**
   * Fetches and decodes the clip up front. Unlike HTMLAudioElement loading,
   * this works on iOS without a user gesture and resolves reliably.
   */
  async load(clipUri: string): Promise<void> {
    if (this.buffers.has(clipUri)) return;

    const res = await fetch(clipUri);
    if (!res.ok) {
      throw new Error(`Failed to fetch audio clip: ${clipUri} (${res.status})`);
    }
    const data = await res.arrayBuffer();
    const buffer = await this.decode(data);
    this.buffers.set(clipUri, buffer);
  }

  async loadAll(clipUris: string[]): Promise<void> {
    await Promise.all(clipUris.map((uri) => this.load(uri)));
  }

  play(clipUri: string, volume = 1, playbackRate = 1): void {
    const buffer = this.buffers.get(clipUri);
    if (!buffer) return;

    // Best-effort resume in case unlock() wasn't called; this only succeeds
    // if we're currently inside a user gesture.
    if (this.ctx.state === 'suspended') {
      void this.ctx.resume();
    }

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = playbackRate;

    const gain = this.ctx.createGain();
    gain.gain.value = volume;

    source.connect(gain);
    gain.connect(this.ctx.destination);
    source.start();
    // AudioBufferSourceNodes are one-shot and garbage-collected after ending;
    // no cleanup needed for short SFX.
  }

  /**
   * Keep the WebAudio listener glued to the head so `playAt` one-shots land in
   * the right place. Call every frame; cheap (a handful of AudioParam writes).
   */
  updateListener(position: { x: number; y: number; z: number }, quaternion: { x: number; y: number; z: number; w: number }) {
    const listener = this.ctx.listener;
    // Forward/up from the head quaternion (inlined to avoid a three import here)
    const { x, y, z, w } = quaternion;
    const fwdX = -(2 * (x * z + w * y));
    const fwdY = -(2 * (y * z - w * x));
    const fwdZ = -(1 - 2 * (x * x + y * y));
    const upX = 2 * (x * y - w * z);
    const upY = 1 - 2 * (x * x + z * z);
    const upZ = 2 * (y * z + w * x);
    if (listener.positionX) {
      listener.positionX.value = position.x;
      listener.positionY.value = position.y;
      listener.positionZ.value = position.z;
      listener.forwardX.value = fwdX;
      listener.forwardY.value = fwdY;
      listener.forwardZ.value = fwdZ;
      listener.upX.value = upX;
      listener.upY.value = upY;
      listener.upZ.value = upZ;
    } else {
      // Legacy Safari-era API
      (listener as any).setPosition(position.x, position.y, position.z);
      (listener as any).setOrientation(fwdX, fwdY, fwdZ, upX, upY, upZ);
    }
  }

  /**
   * Spatialized one-shot: the clip plays FROM a world position — a drive
   * landing 200 yards out should sound 200 yards out, not inside the head.
   * Uses inverse-distance rolloff with a generous refDistance so far landings
   * stay audible but clearly distant.
   */
  playAt(clipUri: string, position: { x: number; y: number; z: number }, volume = 1, playbackRate = 1): void {
    const buffer = this.buffers.get(clipUri);
    if (!buffer) return;
    if (this.ctx.state === 'suspended') void this.ctx.resume();

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = playbackRate;

    const panner = this.ctx.createPanner();
    panner.panningModel = 'HRTF';
    panner.distanceModel = 'inverse';
    panner.refDistance = 8;
    panner.rolloffFactor = 0.9;
    panner.positionX.value = position.x;
    panner.positionY.value = position.y;
    panner.positionZ.value = position.z;

    const gain = this.ctx.createGain();
    gain.gain.value = volume;

    source.connect(gain).connect(panner).connect(this.ctx.destination);
    source.start();
  }

  /** Use the callback form of decodeAudioData: the promise form is missing on older Safari. */
  private decode(data: ArrayBuffer): Promise<AudioBuffer> {
    return new Promise((resolve, reject) => {
      this.ctx.decodeAudioData(data, resolve, (err) => reject(err ?? new Error('decodeAudioData failed')));
    });
  }
}

/** What kind of strike an impact sound should imitate. */
export type ImpactVoice = 'wood' | 'iron' | 'wedge' | 'putt';

/**
 * Synthesized golf impact sounds — no samples to ship, every parameter
 * tunable. A real strike is three simultaneous events, and each is one cheap
 * WebAudio node chain:
 *
 *  1. the CRACK — a few milliseconds of broadband noise through a bandpass
 *     whose centre frequency is the "material" (a driver's titanium face rings
 *     bright ~2.5 kHz, a forged iron sits lower, a wedge lower still);
 *  2. the BODY — a low sine thump, the clubhead mass;
 *  3. the SNAP — a tiny high-frequency click that reads as the ball leaving.
 *
 * Ball speed scales volume, brightens the bandpass and shortens the decay —
 * a chip clicks, a full drive cracks.
 */
export class ImpactSound {
  #player: AudioPlayer;
  #noise?: AudioBuffer;

  constructor(player: AudioPlayer) {
    this.#player = player;
  }

  play(voice: ImpactVoice, ballSpeedMph: number) {
    const ctx = this.#player.context;
    if (ctx.state === 'suspended') void ctx.resume();
    if (ctx.state !== 'running') return;
    const noise = this.#noiseBuffer(ctx);

    // 0..1 across the playable speed range
    const s = Math.min(Math.max(ballSpeedMph / 170, 0), 1);
    const now = ctx.currentTime;

    const spec = {
      wood: { freq: 2400 + 1400 * s, q: 1.1, crack: 0.07, body: 150, bodyGain: 0.5, snap: 0.5 },
      iron: { freq: 1700 + 900 * s, q: 1.4, crack: 0.05, body: 125, bodyGain: 0.45, snap: 0.35 },
      wedge: { freq: 1250 + 600 * s, q: 1.6, crack: 0.04, body: 110, bodyGain: 0.35, snap: 0.2 },
      putt: { freq: 900, q: 2.0, crack: 0.02, body: 90, bodyGain: 0.15, snap: 0.05 },
    }[voice];
    const master = ctx.createGain();
    master.gain.value = 0.25 + 0.75 * s;
    master.connect(ctx.destination);

    // 1. Crack
    {
      const src = ctx.createBufferSource();
      src.buffer = noise;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = spec.freq;
      bp.Q.value = spec.q;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.9, now);
      g.gain.exponentialRampToValueAtTime(0.001, now + spec.crack);
      src.connect(bp).connect(g).connect(master);
      src.start(now);
      src.stop(now + spec.crack + 0.01);
    }
    // 2. Body thump
    {
      const osc = ctx.createOscillator();
      osc.frequency.setValueAtTime(spec.body, now);
      osc.frequency.exponentialRampToValueAtTime(spec.body * 0.6, now + 0.05);
      const g = ctx.createGain();
      g.gain.setValueAtTime(spec.bodyGain, now);
      g.gain.exponentialRampToValueAtTime(0.001, now + 0.06);
      osc.connect(g).connect(master);
      osc.start(now);
      osc.stop(now + 0.07);
    }
    // 3. Snap — the ball leaving the face
    {
      const src = ctx.createBufferSource();
      src.buffer = noise;
      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.value = 5200;
      const g = ctx.createGain();
      g.gain.setValueAtTime(spec.snap, now);
      g.gain.exponentialRampToValueAtTime(0.001, now + 0.012);
      src.connect(hp).connect(g).connect(master);
      src.start(now);
      src.stop(now + 0.02);
    }
  }

  #noiseBuffer(ctx: AudioContext): AudioBuffer {
    if (this.#noise) return this.#noise;
    const length = Math.floor(ctx.sampleRate * 0.15);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
    this.#noise = buffer;
    return buffer;
  }
}

/**
 * A quiet outdoor bed: looped filtered noise whose level follows the wind
 * setting — the range is silent indoors otherwise, and silence reads as
 * "screensaver", not "outside". Deliberately subtle even at 20 mph.
 */
export class WindAmbience {
  #player: AudioPlayer;
  #gain?: GainNode;
  #started = false;

  constructor(player: AudioPlayer) {
    this.#player = player;
  }

  /** Call after the context is unlocked. Safe to call repeatedly. */
  start() {
    if (this.#started) return;
    const ctx = this.#player.context;
    if (ctx.state !== 'running') return;
    this.#started = true;

    // 2 s of pink-ish noise (integrated white), looped
    const length = Math.floor(ctx.sampleRate * 2);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < length; i++) {
      last = last * 0.97 + (Math.random() * 2 - 1) * 0.03;
      data[i] = last * 8;
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 650;
    this.#gain = ctx.createGain();
    this.#gain.gain.value = 0;
    src.connect(lp).connect(this.#gain).connect(ctx.destination);
    src.start();
  }

  /** Level follows the wind: barely-there at calm, a presence at 20 mph. */
  setWind(mph: number) {
    if (!this.#gain) return;
    const ctx = this.#player.context;
    const target = Math.min(0.012 + mph * 0.0035, 0.09);
    this.#gain.gain.setTargetAtTime(target, ctx.currentTime, 0.5);
  }
}